import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { formatDateWithoutTimezoneToFormat } from "@/lib/api/date";
import { parseLayerDate } from "@/section/otc/contract-form-v3/snapshotToForm";
import { OtcContractLayerDto, OtcLayerInvoiceFeeInput } from "@/lib/api/otc";

/** Field-level validation errors keyed by input name. */
export type OtcLayerInvoiceFeeFormErrors = Partial<
  Record<keyof OtcLayerInvoiceFeeInput, string>
>;

/** Empty form values for a fresh create. */
export function emptyInvoiceFeeValues(): OtcLayerInvoiceFeeInput {
  return {
    has_been_paid: false,
    invoice_number: "",
    invoice_date: null,
    date_marked: null,
  };
}

/** Seeds form values from the layer's existing invoice fee info. */
export function layerToInvoiceFeeValues(
  layer: OtcContractLayerDto,
): OtcLayerInvoiceFeeInput {
  const info = layer.invoice_fee_info;
  return {
    has_been_paid: info.has_been_paid,
    invoice_number: info.invoice_number,
    invoice_date: info.invoice_date ? apiDateToPicker(info.invoice_date)?.toISOString() ?? null : null,
    date_marked: info.date_marked ? apiDateToPicker(info.date_marked)?.toISOString() ?? null : null,
  };
}

export function formInvoiceFeeValuesToApi(
  info: OtcLayerInvoiceFeeInput,
): OtcLayerInvoiceFeeInput {
  return {
    has_been_paid: info.invoice_number.trim().length > 0,
    invoice_number: info.invoice_number,
    invoice_date: info.invoice_date ? pickerToApiDate(new Date(info.invoice_date)) : info.invoice_date,
    date_marked: info.date_marked ? pickerToApiDate(new Date(info.date_marked)) : info.date_marked,
  };
}

/**
 * Parses a portal-date string (interpreted in CommonDestinationTimezone) into a
 * local-midnight Date for the picker, so the displayed calendar day matches the
 * stored day regardless of the browser's timezone. Reuses the v3 layer parser.
 */
function apiDateToPicker(value: string | null): Date | undefined {
  if (!value) return undefined;
  return parseLayerDate(value);
}

function localDateToPicker(value: string | null): Date | undefined {
  if (!value) return undefined;
  return new Date(value);
}

/**
 * Serializes a picker Date (whose local Y/M/D is what the user chose) into the
 * `YYYY-MM-DD` portal-date string, taking the local calendar day directly.
 */
function pickerToApiDate(date: Date | undefined): string | null {
  if (!date) return null;
  return formatDateWithoutTimezoneToFormat(date, "YYYY-MM-DD");
}

interface OtcLayerInvoiceFeeFormProps {
  values: OtcLayerInvoiceFeeInput;
  onChange: (values: OtcLayerInvoiceFeeInput) => void;
  errors?: OtcLayerInvoiceFeeFormErrors;
  disabled?: boolean;
}

/**
 * Controlled invoice-fee form. Holds no internal state; the parent owns
 * `values` and receives every change via `onChange`. Reused by the single
 * create, bulk create, and update modals.
 */
export function OtcLayerInvoiceFeeForm({
  values,
  onChange,
  errors,
  disabled,
}: OtcLayerInvoiceFeeFormProps) {
  const setField = <K extends keyof OtcLayerInvoiceFeeInput>(
    key: K,
    value: OtcLayerInvoiceFeeInput[K],
  ) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Invoice #</Label>
          <Input
            type="text"
            disabled={disabled}
            value={values.invoice_number}
            onChange={(e) => setField("invoice_number", e.target.value)}
          />
          {errors?.invoice_number && (
            <span className="text-xs text-destructive">
              {errors.invoice_number}
            </span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Invoice Date</Label>
          <DatePicker
            value={localDateToPicker(values.invoice_date)}
            onChange={(date: Date | undefined) =>
              setField("invoice_date", date?.toISOString() ?? null)
            }
            disabled={disabled}
          />
          {errors?.invoice_date && (
            <span className="text-xs text-destructive">
              {errors.invoice_date}
            </span>
          )}
        </div>

        <div className="grid items-center gap-1.5">
          <Label className="font-semibold">Date Created</Label>
          <DatePicker
            value={localDateToPicker(values.date_marked)}
            onChange={(date: Date | undefined) =>
              setField("date_marked", date?.toISOString() ?? null)
            }
            disabled={disabled}
          />
          {errors?.date_marked && (
            <span className="text-xs text-destructive">
              {errors.date_marked}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
